import React from 'react';
import { Helmet } from 'react-helmet';
import { FaShareAlt, FaRegHeart, FaStar, FaMapMarkerAlt, FaPhoneAlt, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Base from './Base';

// Helper: taller portrait from Unsplash so full head shows (sar na katay)
const getDoctorImageUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('unsplash.com')) {
    const base = url.split('?')[0];
    return `${base}?w=520&h=700&fit=crop&crop=entropy&q=85`;
  }
  return url;
};

function Doctors() {
  const doctorsList = [
    {
      name: "Dr. Sudhair Alam",
      specialty: "Neurologist",
      degree: "MBBS, MD, DM, FCPS",
      address: "Aman Medical Complex, Sakwar, Gilgit",
      rating: "5.0",
      img: "/assets/images/sudhair.jpeg"
    },
    {
      name: "Dr. Iram Javed",
      specialty: "Cardiologist",
      degree: "MBBS, FCPS",
      address: "Aman Medical Complex, Sakwar, Gilgit",
      rating: "5.0",
      img: "/assets/images/Iram.png"
    },
    {
      name: "Dr. Shaista Ahmad",
      specialty: "Gynocologist",
      degree: "MBBS, DM, FCPS",
      address: "Aman Medical Complex, Sakwar, Gilgit",
      rating: "5.0",
      img: "/assets/images/Shaisa.png"
    },
    {
      name: "Dr. Sajid Rauf",
      specialty: "Surgeon",
      degree: "MBBS, MD, DM, FCPS",
      address: "Aman Medical Complex, Sakwar, Gilgit",
      rating: "5.0",
      img: "/assets/images/Sajid.jpg"
    },
  ];

  return (
    <Base>
      <div className="dark:bg-dark-800 min-h-screen transition-colors duration-200">
        <Helmet>
          <title>Our Doctors | Aman Medical Complex Gilgit</title>
          <meta name="description" content="Meet our expert team of board-certified doctors and medical specialists at Aman Medical Complex Gilgit. Top-rated doctors in various specialties." />
          <meta name="keywords" content="doctors gilgit, medical specialists, cardiologist, neurologist, pediatrician, expert doctors pakistan" />
          <meta property="og:title" content="Our Doctors | Aman Medical Complex Gilgit" />
          <meta property="og:description" content="Expert medical professionals dedicated to providing the best healthcare services." />
        </Helmet>

        {/* Page Title Section */}
        <section className="relative py-24 overflow-hidden bg-gradient-to-br from-primary-50 to-white dark:from-dark-800 dark:to-dark-900">
          <div className="absolute inset-0 z-0 opacity-5">
            <div className="absolute top-10 left-10 w-72 h-72 bg-primary-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-primary-300 rounded-full blur-3xl"></div>
          </div>
          <div className="container mx-auto px-4 text-center relative z-10">
            <span className="text-primary-500 font-bold uppercase tracking-[0.2em] text-xs md:text-sm inline-block mb-4">OUR EXPERTS</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-navy-500 dark:text-white mb-6 tracking-tight">Meet Our Experts</h1>
            <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 font-semibold text-sm">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-primary-500">Doctors</span>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 container mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {doctorsList.map((doc, i) => (
            <div key={i} className="bg-white dark:bg-dark-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl border border-gray-100 dark:border-dark-700 group transition-all duration-300 hover:-translate-y-1">
              {/* Doctor Image - same as Home: full width, compact height */}
              <div className="relative w-full h-64 overflow-hidden bg-gray-100 dark:bg-dark-700 shrink-0">
                <img
                  src={getDoctorImageUrl(doc.img)}
                  alt={doc.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  style={{ objectPosition: 'center 20%' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=520&h=700&fit=crop&q=85';
                  }}
                />
                <div className="absolute top-3 left-3 right-3 flex justify-between items-start opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button type="button" className="w-9 h-9 bg-white/95 dark:bg-dark-800 rounded-lg flex items-center justify-center text-primary-500 shadow-lg hover:bg-primary-500 hover:text-white transition-all transform hover:scale-110 active:scale-95 border-none">
                    <FaShareAlt size={14} />
                  </button>
                  <button type="button" className="w-9 h-9 bg-white/95 dark:bg-dark-800 rounded-lg flex items-center justify-center text-primary-500 shadow-lg hover:bg-primary-500 hover:text-white transition-all transform hover:scale-110 active:scale-95 border-none">
                    <FaRegHeart size={14} />
                  </button>
                </div>
              </div>

              {/* Doctor Content - same sizing as Home page */}
              <div className="p-4">
                <div className="flex justify-between items-center gap-2 mb-2">
                  <span className="bg-primary-50 dark:bg-primary-900/25 text-primary-600 dark:text-primary-400 px-3 py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-wider">{doc.specialty}</span>
                  <div className="flex items-center gap-1">
                    <FaStar className="text-amber-400 text-xs" />
                    <span className="text-xs text-gray-700 dark:text-gray-300 font-bold">{doc.rating}</span>
                  </div>
                </div>

                <h3 className="text-base font-black text-navy-500 dark:text-white mb-0.5 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors leading-tight">
                  {doc.name}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 font-medium text-[10px] mb-2 uppercase tracking-wider">{doc.degree}</p>

                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-[10px] font-medium mb-3">
                  <FaMapMarkerAlt className="text-primary-500 flex-shrink-0 mt-0.5" />
                  <span className="leading-snug line-clamp-2">{doc.address}</span>
                </div>

                <div className="flex items-center gap-2 border-t border-gray-100 dark:border-dark-700 pt-3">
                  <Link to="/appointment" className="group/btn flex-1 h-9 bg-primary-50 dark:bg-primary-900/25 text-primary-600 dark:text-primary-400 rounded-xl font-bold text-[10px] flex items-center justify-center gap-1.5 hover:bg-primary-500 hover:text-white dark:hover:bg-primary-500 dark:hover:text-white transition-all duration-300">
                    Book Now <FaArrowRight size={9} className="group-hover/btn:translate-x-0.5 transition-transform" />
                  </Link>
                  <Link to="/appointment" className="h-9 w-9 bg-primary-500 hover:bg-primary-600 text-white rounded-xl flex items-center justify-center transition-all duration-300 shadow-md active:scale-95 flex-shrink-0">
                    <FaPhoneAlt size={10} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </section>
      </div>
    </Base>
  );
}

export default Doctors;
